import { Box } from '@mui/material';
import { Dispatch, SetStateAction, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Category } from 'entities/Category';
import { FilterDropDown, type FilterOption } from 'shared/ui/FilterDropDown';
import { SearchField } from 'shared/ui/SearchField/SearchField';

interface CustomListDetailFiltersProps {
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    categoryFilter: string;
    setCategoryFilter: (value: string) => void;
    categoryOptions: Category[];
}

export const CustomListDetailFilters = memo(
    ({
        search,
        setSearch,
        categoryFilter,
        setCategoryFilter,
        categoryOptions,
    }: CustomListDetailFiltersProps) => {
        const { t } = useTranslation();

        const filterOptions: FilterOption[] = useMemo(
            () =>
                categoryOptions.map((cat) => ({
                    value: cat.id,
                    label: cat.title,
                })),
            [categoryOptions],
        );

        const selectedCategoryIds = useMemo(
            () => (categoryFilter === 'all' ? [] : [categoryFilter]),
            [categoryFilter],
        );

        const handleCategoryChange = useCallback(
            (ids: string[]) => {
                setCategoryFilter(ids[0] ?? 'all');
            },
            [setCategoryFilter],
        );

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '16px',
                    flexShrink: 0,
                    minWidth: 0,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SearchField
                        value={search}
                        setValue={setSearch}
                        placeholder={t('customLists.searchList')}
                        size="small"
                        data-testclass="custom-list-search"
                        sx={{
                            height: '40px',
                            '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '6px',
                            },
                        }}
                    />
                </Box>
                <FilterDropDown
                    id="custom-list-category-filter"
                    label={t('Category')}
                    options={filterOptions}
                    value={selectedCategoryIds}
                    setValue={handleCategoryChange}
                    selectionMode="single"
                    sx={{
                        flexShrink: 0,
                        width: 'max-content',
                        minWidth: '156px',
                        maxWidth: '45%',
                    }}
                />
            </Box>
        );
    },
);

CustomListDetailFilters.displayName = 'CustomListDetailFilters';
